import { useNavigate } from '@tanstack/react-router';

import { contactsApi } from '@/api/queries/contacts';
import type { Contact } from '@/api/types/contacts';
import { useCurrentUser } from '@/auth/useAuth';
import { Button, Icon, useToast } from '@/components/ui';
import { useAppForm } from '@/components/form';

import {
  contactFormSchema,
  contactToFormValues,
  defaultFormValues,
  formValuesToContact,
} from '../contactSchema';
import { useCanManageContacts } from '../useCanManageContacts';
import { useContactDeleteActions } from '../list/useContactDeleteActions';
import { ContactDetailHero } from './ContactDetailHero';
import { ContactDetailSide } from './ContactDetailSide';
import { ContactNotesAside } from './ContactNotesAside';
import { SectionsMenu } from './SectionsMenu';
import { CommentsSection } from './sections/CommentsSection';
import { useSectionLayout } from './sections/useSectionLayout';

export function ContactForm({ contactData }: { contactData?: Contact }) {
  const navigate = useNavigate();
  const toast = useToast();
  const createContact = contactsApi.useCreate();
  const updateContact = contactsApi.useUpdate();
  const canManage = useCanManageContacts();
  const { handleDeleteSingle } = useContactDeleteActions();
  const {
    visibleSections,
    emptySections,
    enableSection,
    disableSection,
    reorderSections,
  } = useSectionLayout(contactData);
  const user = useCurrentUser();

  const form = useAppForm({
    defaultValues: contactData ? contactToFormValues(contactData) : defaultFormValues,
    validators: {
      onChange: contactFormSchema,
    },
    onSubmitInvalid: ({ formApi }) => {
      // The form silently refuses to submit when any field fails validation —
      // surface it so the button never looks dead, and log the field map so the
      // offending field is discoverable.
      console.warn('Contact form validation failed', formApi.state.errors);
      toast.show({ title: 'Please fix the highlighted fields', severity: 'error' });
    },
    onSubmit: async ({ value }) => {
      const contact = formValuesToContact(value, contactData);
      try {
        if (contactData?.id) {
          const etag = contactData.audit?.version ? `"${contactData.audit.version}"` : `"1"`;
          await updateContact.mutateAsync({ id: contactData.id, contact, etag });
          toast.show({ title: 'Contact saved', severity: 'success' });
        } else {
          const created = await createContact.mutateAsync({ contact, source: 'manual_entry' });
          toast.show({ title: 'Contact created', severity: 'success' });
          if (created.id) navigate({ to: '/contacts/$id', params: { id: created.id } });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save contact';
        toast.show({ title: 'Save failed', description: message, severity: 'error' });
      }
    },
  });

  const handleAddNote = () => {
    enableSection('notes');
    form.pushFieldValue('notes', {
      text: '',
      category: '',
      addedAt: new Date().toISOString(),
      addedBy: user?.username || user?.email || '',
    });
    requestAnimationFrame(() =>
      document
        .getElementById('section-notes')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  };

  return (
    <form.AppForm>
      <form.FormRoot>
        <div className="detail">
          <div className="detail-main">
            <form.Subscribe selector={(s) => s.values}>
              {(values) => (
                <ContactDetailHero
                  values={values}
                  contactId={contactData?.id}
                  rawContact={contactData}
                  onAddNote={handleAddNote}
                  canDelete={canManage && !!contactData?.id}
                  onDelete={
                    contactData
                      ? () =>
                          handleDeleteSingle(contactData, () =>
                            navigate({ to: '/contacts', search: { page: 0, pageSize: 25 } }),
                          )
                      : undefined
                  }
                  sectionsMenu={
                    <SectionsMenu
                      visibleSections={visibleSections}
                      emptySections={emptySections}
                      contact={contactData}
                      onReorder={reorderSections}
                      onDisable={disableSection}
                      onEnable={enableSection}
                    />
                  }
                />
              )}
            </form.Subscribe>

            {visibleSections.map(({ key, Component }) => (
              <div key={key} id={`section-${key}`} className="scroll-mt-4">
                <Component form={form} />
              </div>
            ))}

            {/* Review (and Status & preferences / General note) live in the aside
                (ContactDetailSide); only the comments thread stays in the main column. */}
            {contactData?.id && <CommentsSection contact={contactData} />}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Button variant="secondary" onClick={() => navigate({ to: '/contacts', search: { page: 0, pageSize: 25 } })}>
                Back to contacts
              </Button>
              <form.SubmitButton variant="primary" startIcon={<Icon name="check" size={13} />}>
                {contactData ? 'Save changes' : 'Create contact'}
              </form.SubmitButton>
            </div>
          </div>

          <ContactDetailSide contact={contactData} form={form} />

          {/* Third column on very wide screens (≥1440px): read-only Notes list.
              Hidden below that breakpoint via .detail-notes in tokens.css. */}
          <ContactNotesAside contact={contactData} />
        </div>
      </form.FormRoot>
    </form.AppForm>
  );
}
